import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery } from 'react-query';
import { Box, Switch, Typography } from '@mui/material';
import HLButton from 'components/HLButton';
import TabComponent, { TabContext } from 'components/Common/TabComponent';
import HLDropdown from 'components/HLDropdown';
import { ToastType } from 'components/Common/Toast';
import { useHttp } from 'hooks/use-fetch';
import { PatientCtx } from 'pages/Dashboard/constants';
import { Logger } from 'utils/logger';
import {
  allergiesUrls,
  backgroundsUrls,
  diagnosisUrls,
  familyHistoryUrls,
  healthRecordRefreshUrls,
  hospitalizationsUrls,
  labsUrls,
  lifestyleUrls,
  medicationsUrls,
  patientProviderUrls,
  patientUrls,
  symptomsUrls,
  trackingUrls,
} from 'utils/apiUrls';
import {
  fetchDiagnosisListCombined,
} from 'pages/Dashboard/services/diagnosis.services';
import {
  fetchAllMedicationList,
  fetchMedicationChartHistory,
} from 'pages/Dashboard/services/medications.services';
import { fetchPatientDetails, fetchPatientHealthRecordStatus } from 'pages/Dashboard/services/patient.services';
import fetchFamilyHistoryList from 'pages/Dashboard/services/familyhistory.services';
import {
  fetchCurrentSymptomList,
  fetchSymptomList,
} from 'pages/Dashboard/services/symptoms.services';
import fetchAllergyList from 'pages/Dashboard/services/allergies.services';
import fetchHospitalizationList from 'pages/Dashboard/services/hospitalizations.services';
import { fetchLabList } from 'pages/Dashboard/services/labs.services';
import { fetchLifeStyleList } from 'pages/Dashboard/services/lifestyle.services';
import fetchBackgroundList from 'pages/Dashboard/services/backgrounds.services';
import {
  fetchAllPatientProvidersList,
} from 'pages/Dashboard/services/patientprovider.services';
import {
  defaultWaypointTracking,
  TimeFrameOptions,
} from 'pages/Dashboard/utils/trackingUtils';
import ProgressMonitor from 'pages/Dashboard/ProgressMonitor';
import {
  fetchWaypointsTracking,
} from 'pages/Dashboard/services/tracking.services';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import ContentLoader from 'components/Common/ContentLoader';
import { WaypointTrackingAPIResponse, WaypointType } from 'pages/Dashboard/types/waypoints.types';
import ScrollIndicator from 'components/Common/ScrollIndicator';
import HealthRecordNew from 'pages/Dashboard/HealthRecordsNew';
import { PatientRecordRetrievalStatus, Session, Status } from 'pages/Dashboard/types';
import { fetchSessionList } from 'pages/Dashboard/services/sessionrecording.services';
import Sessions from 'pages/Dashboard/components/SessionRecording/Sessions';
import ConfirmPatientDrawer from 'pages/Dashboard/components/ConfirmPatientDrawer';
import PatientDetailsPanel from 'components/PatientDetailsPanel';
import { HrRefreshHistoryStatus } from 'pages/Dashboard/types/healthRecords.types';
import { healthRecordRefreshHistory } from 'pages/Dashboard/services/healthrecords.services';
import HLCard from 'components/HLCard';

export default function PatientDetailNew() {
  const { id } = useParams();
  const location = useLocation();
  const { tab: tabState, refreshId, hrCount } = location.state || {};
  const {
    http,
    providerId,
    isSessionRecordingEnabled,
    providerName: loggedInProvider,
    selectedProviderName,
    isDemoUser,
    isHrRefreshEnabled,
    setToastProps,
  } = useHttp();

  const [selectTimeFrame, setSelectTimeFrame] = React.useState(
    TimeFrameOptions.LAST_3_MONTHS as string,
  );

  const [selectedChip, setSelectedChip] = React.useState(false);
  const [refreshIdToUse, setRefreshIdToUse] = React.useState(null);
  const [isProgressMonitorDataFetched, setIsProgressMonitorDataFetched] = React.useState(false);

  const [selectedTab, setSelectedTab] = React.useState(0);

  const [sessions, setSessions] = React.useState<Session[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [cb, setCb] = React.useState<() => void>(() => null);
  const [recordingStatus, setRecordingStatus] = React.useState<Status>(Status.DEFAULT);
  const [isEditingNotes, setIsEditingNotes] = React.useState(false);

  const [showConfirmDrawer, setShowConfirmDrawer] = React.useState(false);

  React.useEffect(() => {
    setSelectedTab(typeof tabState === 'number' ? tabState : 0);
  }, [tabState]);

  const onChangeTimeFrame = React.useCallback((timeFrameValue: string) => {
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_MOOD_AND_WAYPOINT_TIME_FRAME_CHANGED, {
      'patient id': id ?? '',
      'time frame': timeFrameValue,
      location: 'progress_monitor_tab_details_view_charts_data_from_past',
    }, true);
    setSelectTimeFrame(timeFrameValue);
  }, [id]);

  const onError = (message: string) => {
    Logger.error(message);
    setToastProps({
      message,
      open: true,
      type: ToastType.error,
    });
  };

  const {
    data: patientDetails,
    isFetching: patientDetailsFetching,
    isError: patientDetailsError,
    refetch: refetchPatientDetails,
  } = useQuery(
    [patientUrls.patientDetails.queryUrl, providerId, id],
    {
      queryFn: fetchPatientDetails(http.get),
      onError: () => onError('Error fetching patient details'),
      enabled: !!providerId && !!id,
    },
  );

  const isPendingApproval = ['PATIENT_APPROVAL_STATUS_PENDING', 'PATIENT_APPROVAL_STATUS_REJECTED'].includes(patientDetails?.patient?.approvalStatus ?? '');

  const medications = useQuery({
    queryKey: [medicationsUrls.listMedications.queryUrl, providerId, id, selectedChip
      ? refreshIdToUse : undefined],
    queryFn: fetchAllMedicationList(http.get),
    onError: () => onError('Error fetching patient medications'),
    enabled: !patientDetailsFetching && !isPendingApproval,
  });

  const enableQueries = React.useMemo(
    () => !medications?.isFetching
      && !patientDetailsFetching
      && !isPendingApproval
    && (isProgressMonitorDataFetched || selectedTab === 1),
    [
      medications?.isFetching,
      patientDetailsFetching,
      isPendingApproval,
      selectedTab,
      isProgressMonitorDataFetched,
    ],
  );

  const medicationChartHistory = useQuery({
    queryKey: [medicationsUrls.medicationHistory.queryUrl, providerId, id, selectedChip
      ? refreshIdToUse : undefined],
    queryFn: fetchMedicationChartHistory(http.get),
    onError: () => onError('Error fetching patient medication chart history'),
  });

  const [
    diagnosis,
    familyHistory,
    symptoms,
    currentSymptoms,
    allergies,
    hospitalizations,
    labs,
    patientProviders,
    lifestyle,
    backgrounds,
  ] = useQueries([
    {
      queryKey: [
        diagnosisUrls.listDiagnosis.queryUrl,
        providerId,
        id,
        selectedChip ? refreshIdToUse : undefined,
      ],
      queryFn: fetchDiagnosisListCombined(http.get),
      onError: () => onError('Error fetching patient diagnosis'),
      enabled: enableQueries,
    },
    {
      queryKey: [
        familyHistoryUrls.listFamilyHistory.queryUrl,
        providerId,
        id,
        selectedChip ? refreshIdToUse : undefined,
      ],
      queryFn: fetchFamilyHistoryList(http.get),
      onError: () => onError('Error fetching patient family history'),
      enabled: enableQueries,
    },
    {
      queryKey: [symptomsUrls.listSymptoms.queryUrl, providerId, id],
      queryFn: fetchSymptomList(http.get),
      onError: () => onError('Error fetching patient symptoms'),
      enabled: enableQueries,
    },
    {
      queryKey: [symptomsUrls.listSymptoms.queryUrl, providerId, id, 'current'],
      queryFn: fetchCurrentSymptomList(http.get),
      onError: () => onError('Error fetching patient current symptoms'),
      enabled: enableQueries,
    },
    {
      queryKey: [
        allergiesUrls.listAllergies.queryUrl,
        providerId,
        id,
        selectedChip ? refreshIdToUse : undefined,
      ],
      queryFn: fetchAllergyList(http.get),
      onError: () => onError('Error fetching patient allergies'),
      enabled: enableQueries,
    },
    {
      queryKey: [
        hospitalizationsUrls.listHospitalizations.queryUrl,
        providerId,
        id,
        selectedChip ? refreshIdToUse : undefined,
      ],
      queryFn: fetchHospitalizationList(http.get),
      onError: () => onError('Error fetching patient hospitalizations'),
      enabled: enableQueries,
    },
    {
      queryKey: [
        labsUrls.listLabs.queryUrl,
        providerId,
        id,
        selectedChip ? refreshIdToUse : undefined,
      ],
      queryFn: fetchLabList(http.get),
      onError: () => onError('Error fetching patient labs'),
      enabled: enableQueries,
    },
    {
      queryKey: [
        patientProviderUrls.listPatientProviders.queryUrl,
        providerId,
        id,
        selectedChip ? refreshIdToUse : undefined,
      ],
      queryFn: fetchAllPatientProvidersList(http.get),
      onError: () => onError('Error fetching patient providers'),
      enabled: enableQueries,
    },
    {
      queryKey: [lifestyleUrls.listLifestyle.queryUrl, providerId, id],
      queryFn: fetchLifeStyleList(http.get),
      onError: () => onError('Error fetching patient lifestyle'),
      enabled: enableQueries,
    },
    {
      queryKey: [backgroundsUrls.listBackgrounds.queryUrl, providerId, id],
      queryFn: fetchBackgroundList(http.get),
      onError: () => onError('Error fetching patient backgrounds'),
      enabled: enableQueries,
    },
  ]);

  const healthRecordStatus = useQuery(
    [`${patientUrls.patientHealthRecordStatus.queryUrl}`, providerId, id],
    {
      queryFn: fetchPatientHealthRecordStatus(http.get),
    },
  );

  const getHrRefreshHistory = useQuery({
    queryKey: [healthRecordRefreshUrls.hrRefreshHistory.queryUrl, providerId, id],
    queryFn: healthRecordRefreshHistory(http.get),
    enabled: isHrRefreshEnabled,
  });

  const isRecordStatusNotRetrieved = healthRecordStatus.data?.status?.recordRetrievalStatus
      !== PatientRecordRetrievalStatus.PATIENT_RECORD_RETRIEVAL_STATUS_RETRIEVED;

  const currentWaypoint = useQuery(
    [`${trackingUrls.waypoints.queryUrl}`, providerId, id, selectTimeFrame, 'current'],
    {
      queryFn: fetchWaypointsTracking(http.get),
      onError: () => onError('Error fetching patient waypoint'),
      staleTime: 5 * 60 * 1000, // 5 minutes
      select: (data) => {
        const filteredWaypoints = data?.waypoints.filter(
          (waypoint) => waypoint.waypoint.type !== WaypointType.WAYPOINT_TYPE_APPLE_HEALTH,
        );

        return {
          waypoints: filteredWaypoints,
        } as WaypointTrackingAPIResponse;
      },
    },
  );

  const previousWaypoint = useQuery(
    [`${trackingUrls.waypoints.queryUrl}`, providerId, id, selectTimeFrame, 'previous'],
    {
      queryFn: fetchWaypointsTracking(http.get),
      onError: () => onError('Error fetching patient waypoint'),
      staleTime: 5 * 60 * 1000, // 5 minutes
      select: (data) => {
        const filteredWaypoints = data?.waypoints.filter(
          (waypoint) => !(waypoint.waypoint.type === WaypointType.WAYPOINT_TYPE_DERIVED
            || waypoint.waypoint.type === WaypointType.WAYPOINT_TYPE_APPLE_HEALTH),
        );

        return {
          waypoints: filteredWaypoints,
        } as WaypointTrackingAPIResponse;
      },
    },
  );

  const { mutate: fetchSessions, isLoading: isFetching } = useMutation(
    ({ offset }: { offset: number }) => fetchSessionList(http.get, providerId, id ?? '', `?offset=${offset}&limit=10`),
    {
      onSuccess: (data) => {
        setTotal(+data.pagination.total);
        if (data.sessions.length > 0) {
          setSessions((prev) => [
            ...(data.pagination.offset - 10 > 0 ? prev : []),
            ...data.sessions,
          ]);
        }
      },
    },
  );

  React.useEffect(() => {
    const handleCopy = () => {
      trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_COPY_EVENT, {
        'patient id': id ?? '',
        'provider id': providerId ?? '',
      });
    };

    document.addEventListener('copy', handleCopy);

    return () => {
      document.removeEventListener('copy', handleCopy);
    };
  }, [id, providerId, loggedInProvider, selectedProviderName]);

  const refreshIdFromHistory = getHrRefreshHistory?.data?.refreshHistory[0]?.refreshId;

  React.useEffect(() => {
    if (refreshIdFromHistory || refreshId) {
      const newRefreshId = refreshId || refreshIdFromHistory;
      setRefreshIdToUse(newRefreshId);
    }
  }, [refreshId, refreshIdFromHistory, getHrRefreshHistory.data, setRefreshIdToUse]);

  React.useEffect(() => {
    const { refreshId: localRefreshId, notificationId } = location.state || {};
    // Detect if page was refreshed (sessionStorage flag)
    const isPageReloaded = sessionStorage.getItem('wasPageReloaded') === 'true';
    if (isPageReloaded) {
      // If reloaded, clear state and reset chip
      setSelectedChip(false);
      sessionStorage.removeItem('selectedChip');
      sessionStorage.removeItem('wasPageReloaded');
    }
    else if (localRefreshId && notificationId && hrCount > 0) {
      // If navigated from notification, select chip and store state when hrCount > 0
      setSelectedChip(true);
      sessionStorage.setItem('selectedChip', 'true');
    }
    else if (localRefreshId && notificationId && hrCount === 0) {
      // If navigated from notification, select chip and store state when hrCount = 0
      setSelectedChip(false);
      sessionStorage.setItem('selectedChip', 'false');
    }
    const handleBeforeUnload = () => {
      if (window.location.pathname === `/patient/${id}`) {
        sessionStorage.setItem('wasPageReloaded', 'true');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.state, id, hrCount, setSelectedChip]);


  // Show the "In Progress HRR" UI in the case when:
  // 1. The record status has *not* been retrieved yet (i.e., still in progress or pending),
  // 2. The system is *not* currently loading (isLoading is false), and
  // 3. There is a refresh history array available, and it has at most one entry.

  const showInProgressHRREnabled = isRecordStatusNotRetrieved
        && getHrRefreshHistory.data?.refreshHistory
        && getHrRefreshHistory.data.refreshHistory.length <= 1;

  // Show the "In Progress HRR" UI as *disabled* (e.g., maybe grayed out) when:
  // 1. The record status has *not* been retrieved,
  // 2. The system is *not* loading, and
  // 3. Either:
  //    a. There is a refresh history with at most one entry, OR
  //    b. The refresh history data is entirely undefined (i.e., not fetched yet)

  const showInProgressHRRDisabled = isRecordStatusNotRetrieved
        && ((getHrRefreshHistory.data?.refreshHistory
          && getHrRefreshHistory.data.refreshHistory.length <= 1)
          || getHrRefreshHistory.data === undefined);

  const isFetchingHR = healthRecordStatus.isLoading
    || getHrRefreshHistory.isLoading
    || showInProgressHRREnabled
    || showInProgressHRRDisabled;

  const setFetchedData = React.useCallback(() => {
    setIsProgressMonitorDataFetched(true);
  }, []);

  const refreshStatus = getHrRefreshHistory.data?.refreshHistory[0]?.status;

  const configureTabs: TabContext[] = [
    {
      header: 'Progress monitor',
      content: (isPendingApproval ? null
        : (
          <Box mt={1.5}>
            <ProgressMonitor
              timeFrame={selectTimeFrame}
              onChangeTimeFrame={onChangeTimeFrame}
              currentWaypointData={
                currentWaypoint?.data?.waypoints ? currentWaypoint.data : defaultWaypointTracking
              }
              previousWaypointData={
                previousWaypoint?.data?.waypoints ? previousWaypoint?.data : defaultWaypointTracking
              }
              patient={patientDetails?.patient}
              setFetchedData={setFetchedData}
            />
          </Box>
        )
      ),
    },
    {
      header: 'Health records',
      content: isPendingApproval ? null : (
        <Box mt={1.5}>
          <HealthRecordNew
            diagnosis={diagnosis}
            medications={medications}
            familyHistory={familyHistory}
            symptoms={symptoms}
            currentSymptoms={currentSymptoms}
            allergies={allergies}
            hospitalizations={hospitalizations}
            providers={patientProviders}
            lifeStyle={lifestyle}
            backgrounds={backgrounds}
            labs={labs}
            medicationChartHistory={medicationChartHistory}
            isFetchingHR={isFetchingHR}
            refreshStatus={refreshStatus}
          />
        </Box>
      ),
    },
    ...(isSessionRecordingEnabled ? [{
      header: 'Sessions',
      content: (
        <Sessions
          sessions={sessions}
          isFetching={isFetching}
          total={total}
          fetchSessions={fetchSessions}
          setToastProps={setToastProps}
        />
      ),
    }] : []),
  ];

  const timeFrameValues = React.useMemo(() => Object.values(TimeFrameOptions), []);

  const selectedTimeFrame = React.useMemo(
    () => timeFrameValues.findIndex((value) => value === selectTimeFrame),
    [selectTimeFrame, timeFrameValues],
  );

  const hrCountFromHistory = getHrRefreshHistory?.data?.refreshHistory[0]?.hrCount ?? 0;

  const onTabChange = (tab: number) => {
    const tabName = configureTabs[tab].header;

    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_TAB_SELECTED, {
      tab: tabName,
    });

    if (tabName === 'SESSIONS') {
      setSessions([]);
      setTotal(0);
    }
    setSelectedTab(tab);
  };

  const deSelectChip = React.useCallback(() => {
    setSelectedChip(false);
    trackMixpanelEvent(MixpanelEventName
      .PATIENT_DETAILS_SHOW_RECORDS_FROM_LAST_REFRESH_BUTTON_DESELECTED, {
      'fetch in progress yes/no': refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS ? 'yes' : 'no',
      'provider id': providerId ?? '',
      'logged in provider name': loggedInProvider ?? '',
      'selected provider name': selectedProviderName ?? '',
      location: 'patient_history_tab_show_records_from_last_refresh',
    });
  }, [refreshStatus, providerId, loggedInProvider, selectedProviderName]);

  const handleChipClick = React.useCallback(() => {
    if (hrCount > 0 || hrCountFromHistory > 0 || !selectedChip) {
      trackMixpanelEvent(
        MixpanelEventName.PATIENT_DETAILS_SHOW_RECORDS_FROM_LAST_REFRESH_BUTTON_SELECTED,
        {
          'number of records': hrCount || hrCountFromHistory,
          'fetch in progress yes/no':
            refreshStatus === HrRefreshHistoryStatus.IN_PROGRESS ? 'yes' : 'no',
          'provider id': providerId ?? '',
          'logged in provider name': loggedInProvider ?? '',
          'selected provider name': selectedProviderName ?? '',
          location: 'patient_history_tab_show_records_from_last_refresh',
        },
      );
      setSelectedChip((prev) => !prev);
    }
  }, [
    hrCount,
    hrCountFromHistory,
    selectedChip,
    refreshStatus,
    providerId,
    loggedInProvider,
    selectedProviderName,
  ]);

  const handleTabsValueChange = React.useCallback((value: number | string) => {
    const timeFrameValue = timeFrameValues[value as number];
    onChangeTimeFrame(timeFrameValue);
    trackMixpanelEvent(MixpanelEventName.PATIENT_DETAILS_DATERANGE_CLICKED, {
      timeFrameValue,
      clickedFrom: 'Patient details',
      location: 'progress_monitor_tab_data_from_past',
    });
  }, [onChangeTimeFrame, timeFrameValues]);

  const contextValue = React.useMemo(
    () => ({
      cb,
      setCb,
      recordingStatus,
      isEditingNotes,
      setIsEditingNotes,
      setRecordingStatus,
      ...patientDetails?.patient,
    }),
    [cb, patientDetails?.patient, recordingStatus, isEditingNotes],
  );

  const showChipText = (hrCount > 0 || hrCountFromHistory > 0) && (getHrRefreshHistory
    .data?.refreshHistory?.length ?? 0) > 1;

  const renderBelowHeaderMap: {
    [key: number]: React.ReactElement | null;
  } = React.useMemo(
    () => ({
      0: (
        <HLDropdown
          value={selectedTimeFrame}
          onChange={(e) => handleTabsValueChange(e.target.value as string | number)}
          options={timeFrameValues.map((val, idx) => ({ label: val, value: idx }))}
          sx={{ minWidth: 159, height: 36 }}
        />
      ),
      1: showChipText ? (
        <Box display='flex' alignItems='baseline' gap={0.5}>
          <Switch
            checked={selectedChip}
            onChange={() => (selectedChip ? deSelectChip() : handleChipClick())}
            color='primary'
            size='small'
            inputProps={{ 'aria-label': 'Recent health record toggle' }}
          />
          <Typography variant='body1' color='text.primary'>
            Recent health record
          </Typography>
        </Box>
      ) : null,
    }),
    [
      deSelectChip,
      handleChipClick,
      handleTabsValueChange,
      selectedChip,
      selectedTimeFrame,
      showChipText,
      timeFrameValues,
    ],
  );

  const firstEntryWithCompletedHRR = getHrRefreshHistory.data?.refreshHistory
    ?.find((entry) => [
      HrRefreshHistoryStatus.COMPLETED,
      HrRefreshHistoryStatus.INTIAL_REQUEST,
    ].includes(entry.status));

  return (
    <ContentLoader
      isFetching={!patientDetails || patientDetailsFetching}
      minHeight={400}
      isError={patientDetailsError}
    >
      <PatientCtx.Provider value={contextValue}>
        <PatientDetailsPanel
          patientDetails={patientDetails}
          recordingStatus={recordingStatus}
          scheduledOn={firstEntryWithCompletedHRR?.scheduledOn || null}
          setSelectedChip={setSelectedChip}
          fetchSessions={fetchSessions}
          setSelectedTab={setSelectedTab}
          hrRefreshHistory={getHrRefreshHistory}
          hrRecordStatus={healthRecordStatus}
          isFetchingHR={isFetchingHR}
        />
        <TabComponent
          tabContext={configureTabs}
          tabsHeight={48}
          defaultTab={selectedTab}
          renderBelowHeader={(tab: number) => renderBelowHeaderMap[tab] ?? null}
          onTabChange={onTabChange}
          isDemoUser={isDemoUser}
          disableAllTabs={isPendingApproval}
        />
        {isPendingApproval && (
          <>
            <HLCard
              sx={{
                height: 520,
                p: 1.5,
                mt: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant='body1'
                fontWeight={600}
                mb={2}
                align='center'
                color='text.primary'
              >
                Patient confirmation needed
              </Typography>
              <Typography variant='body2' mb={2} align='center' color='text.secondary'>
                This patient needs to be confirmed before their
                <br />
                health records can be accessed.
              </Typography>
              <Box>
                <HLButton
                  variant='contained'
                  size='small'
                  color='primary'
                  sx={{ px: 1, py: 0.5 }}
                  onClick={() => {
                    trackMixpanelEvent(
                      MixpanelEventName.PATIENT_DETAIL_CONFIRM_PATIENT_BUTTON_CLICKED,
                      { 'patient id': patientDetails?.patient?.id ?? '' },
                    );
                    setShowConfirmDrawer(true);
                  }}
                >
                  CONFIRM PATIENT
                </HLButton>
              </Box>
            </HLCard>
            <ConfirmPatientDrawer
              open={showConfirmDrawer}
              onClose={() => {
                refetchPatientDetails();
                setShowConfirmDrawer(false);
              }}
              patient={patientDetails?.patient ?? {}}
            />
          </>
        )}
        <ScrollIndicator />
      </PatientCtx.Provider>
    </ContentLoader>
  );
}
